import { useEndEnrollment, useResumeEnrollment, useUnenrollUser } from '@actions';
import { ReactComponent as FlagIcon } from '@assets/icons/V4/flag-icon.svg';
import { ReactComponent as NoDataIcon } from '@assets/icons/V4/no-data.svg';
import { ReactComponent as ResumeIcon } from '@assets/icons/V4/resume-icon.svg';
import { ReactComponent as SettingsIcon } from '@assets/icons/V4/settings-icon.svg';
import { ReactComponent as TrashIcon } from '@assets/icons/V4/trash-icon.svg';
import IconButton from '@components/V4/IconButton';
import Table from '@components/V4/Table/Table';
import Tooltip from '@components/V4/Tooltip';
import { Enrollment, ENROLLMENT_STATUS_TYPES } from '@shared/common';
import { ActiveEnrollmentStatuses, EnrollmentStatusTypes, SessionStatus } from '@shared/enums';
import useClientPagination from 'apps/agora/src/hooks/useClientPagination';
import useToast from 'apps/agora/src/hooks/useToast';
import { getValueOrNA, mergeClassNames } from 'apps/agora/src/utils/helpers';
import { BasicProps } from 'apps/agora/src/utils/types';
import EmptyStateCard from 'apps/agora/src/views/UserView/components/EmptyStateCard';
import moment from 'moment';
import { useContext, useEffect, useMemo, useState } from 'react';
import SessionModalSection, { ActionButton } from '../../../../DetailsModalSection';
import { MentorDetailsPageContext } from '../../../MentorDetailsPage';
import TeachingEnrollmentsForm from './TeachingEnrollmentForm';
import { ExclamationCircleIcon } from '@heroicons/react/outline';

interface TeachingEnrollmentsTableProps extends BasicProps {
  areEnrollmentsActive?: boolean;
  enrollments: Partial<Enrollment>[];
}

const TeachingEnrollmentsTable = (props: TeachingEnrollmentsTableProps) => {
  const { areEnrollmentsActive, enrollments } = props;

  const { courses, onEnrollmentModalOpen, onSessionModalOpen, onStudentModalOpen } =
    useContext(MentorDetailsPageContext);

  const [isAddingEnrollment, setIsAddingEnrollment] = useState(false);

  const toast = useToast();

  const filteredEnrollments = useMemo(
    () =>
      enrollments?.filter((enrollment) =>
        areEnrollmentsActive
          ? ActiveEnrollmentStatuses.includes(
              enrollment.statuses?.at(-1)?.status as EnrollmentStatusTypes
            )
          : !ActiveEnrollmentStatuses.includes(
              enrollment.statuses?.at(-1)?.status as EnrollmentStatusTypes
            )
      ) || [],
    [enrollments, areEnrollmentsActive]
  );

  const [paginatedEndedEnrollments, endedEnrollmentsPagination] = useClientPagination({
    data: filteredEnrollments,
    shouldOnlyShowPages: true,
    initialPageSize: 5,
  });

  const [paginatedActiveEnrollments, activeEnrollmentsPagination] = useClientPagination({
    data: filteredEnrollments,
    shouldOnlyShowPages: true,
    initialPageSize: 5,
  });

  const { mutate: endEnrollment, isLoading: isEndingEnrollment } = useEndEnrollment({
    onSuccess: () => {
      toast.success('Enrollment ended successfully.');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message);
    },
  });

  const { mutate: unenrollUser, isLoading: isEnrollmentDeleting } = useUnenrollUser({
    onSuccess: () => {
      toast.success('Enrollment deleted successfully.');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message);
    },
  });

  const { mutate: resumeEnrollment, isLoading: isResumingEnrollment } = useResumeEnrollment({
    onSuccess: () => {
      toast.success('Enrollment resumed successfully.');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message);
    },
  });

  useEffect(() => {
    setIsAddingEnrollment(false);
  }, [areEnrollmentsActive]);

  const getSectionButtons = () => {
    const buttons: ActionButton[] = [];

    if (areEnrollmentsActive) {
      buttons.push({
        buttonText: 'Add Enrollment',
        onClick: () => setIsAddingEnrollment(true),
      });
    }

    return buttons;
  };

  const getLastCompletedSessionCell = (enrollment: any) => {
    const filteredSessions = enrollment.sessions?.filter((session: any) => {
      const lastStatus = session.statuses[session.statuses.length - 1].value;

      return ![SessionStatus.PENDING, SessionStatus.SCHEDULED, SessionStatus.ONGOING].includes(
        lastStatus
      );
    });

    const sortedSessions = filteredSessions?.sort((a: any, b: any) => {
      const dateA = moment(a.relevantMeetingInfo?.scheduledDate);
      const dateB = moment(b.relevantMeetingInfo?.scheduledDate);

      return dateB.diff(dateA);
    });

    if (sortedSessions?.[0]) {
      return {
        text: moment(sortedSessions[0].relevantMeetingInfo?.scheduledDate).format('DD.MM.YYYY'),
        onClick: sortedSessions[0]?._id
          ? () => onSessionModalOpen(sortedSessions[0]?._id || '')
          : undefined,
      };
    }

    return { text: 'n/a' };
  };

  const endClickHandler = (enrollmentId: string) => {
    endEnrollment({ enrollmentId });
  };

  const resumeClickHandler = (enrollmentId: string) => {
    resumeEnrollment({ enrollmentId });
  };

  const deleteClickHandler = (enrollmentId: string) => {
    unenrollUser({ enrollmentId });
  };

  const isLoading = isEndingEnrollment || isResumingEnrollment || isEnrollmentDeleting;

  const paginatedEnrollments = areEnrollmentsActive
    ? paginatedActiveEnrollments
    : paginatedEndedEnrollments;

  const pagination = areEnrollmentsActive
    ? activeEnrollmentsPagination
    : endedEnrollmentsPagination;

  return isAddingEnrollment ? (
    <TeachingEnrollmentsForm
      onCloseEditForm={() => setIsAddingEnrollment(false)}
      courses={courses}
    />
  ) : (
    <SessionModalSection
      className="mt-6"
      sectionTitle="Teaching"
      buttonsArray={getSectionButtons()}
    >
      {paginatedEnrollments.length ? (
        <Table size="small" pagination={pagination}>
          <Table.Header>
            <Table.Row isHeader>
              <Table.HeaderCell>Enrollment</Table.HeaderCell>
              <Table.HeaderCell>Sessions</Table.HeaderCell>
              <Table.HeaderCell>Student</Table.HeaderCell>
              <Table.HeaderCell>Last Session</Table.HeaderCell>
              <Table.HeaderCell>Comment</Table.HeaderCell>
              <Table.HeaderCell>Graduation Year</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {paginatedEnrollments.map((enrollment) => {
              return (
                <Table.Row key={enrollment._id}>
                  <Table.Cell onClick={() => onEnrollmentModalOpen(enrollment._id ?? '')}>
                    <div className="flex items-center gap-2">
                      <span
                        className="w-[9px] h-[9px] rounded-full"
                        style={{ backgroundColor: enrollment.course?.color }}
                      />
                      {enrollment?.course?.title}
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <Tooltip text="Completed Sessions / Scheduled or Ongoing Sessions / Total No. of Sessions">
                      {`${enrollment?.noOfCompletedSessions}/${enrollment?.noOfScheduledOrInProgressSessions}/${enrollment?.noOfSessions}`}
                    </Tooltip>
                  </Table.Cell>
                  <Table.Cell
                    onClick={
                      enrollment?.user?._id
                        ? () => onStudentModalOpen(enrollment?.user?._id || '')
                        : undefined
                    }
                  >
                    <Tooltip text={enrollment.user?.isDeactivated ? 'Deactivated' : undefined}>
                      <span
                        className={mergeClassNames('flex gap-1', {
                          'line-through': enrollment.user?.isDeactivated,
                        })}
                      >
                        {enrollment.user?.fullName ?? 'n/a'}{' '}
                        {enrollment.user?.isDeactivated && (
                          <ExclamationCircleIcon
                            className="w-4 h-4"
                            aria-label="Deactivated user"
                          />
                        )}
                      </span>
                    </Tooltip>
                  </Table.Cell>
                  <Table.Cell onClick={getLastCompletedSessionCell(enrollment)?.onClick}>
                    {getLastCompletedSessionCell(enrollment).text}
                  </Table.Cell>
                  <Table.Cell className="truncate max-w-10">
                    {enrollment?.comment?.message}
                  </Table.Cell>
                  <Table.Cell>{getValueOrNA(enrollment?.user?.graduationYear)}</Table.Cell>
                  <Table.Cell>
                    {(!!enrollment.statuses?.at(-1)?.status ||
                      enrollment.statuses?.at(-1)?.status === 0) &&
                      ENROLLMENT_STATUS_TYPES[enrollment.statuses.at(-1)!.status]}
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex gap-4">
                      <IconButton
                        isDisabled={isLoading}
                        size="small"
                        variant="ghost"
                        icon={<SettingsIcon />}
                        onClick={() => onEnrollmentModalOpen(enrollment._id ?? '')}
                      />
                      {areEnrollmentsActive ? (
                        <IconButton
                          isDisabled={isLoading}
                          size="small"
                          variant="ghost"
                          color="danger"
                          icon={<FlagIcon />}
                          onClick={() => endClickHandler(enrollment._id ?? '')}
                        />
                      ) : (
                        <IconButton
                          isDisabled={isLoading}
                          size="small"
                          variant="ghost"
                          icon={<ResumeIcon />}
                          onClick={() => resumeClickHandler(enrollment._id ?? '')}
                        />
                      )}
                      <IconButton
                        isDisabled={isLoading}
                        size="small"
                        variant="ghost"
                        color="danger"
                        icon={<TrashIcon />}
                        onClick={() => deleteClickHandler(enrollment._id ?? '')}
                      />
                    </div>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      ) : (
        <EmptyStateCard text="No Existing Data" icon={<NoDataIcon className="text-customGrey" />} />
      )}
    </SessionModalSection>
  );
};

export default TeachingEnrollmentsTable;
